import React from "react";
import { gql, useQuery } from "@apollo/client";
import {
  Customer,
  FindCustomersWhoRedeemedOffersQuery,
  FindCustomersWhoRedeemedOffersQueryVariables,
} from "../../../schemaTypes";

export const FIND_CUSTOMERS_WHO_REDEEMED_OFFERS = gql`
  query FindCustomersWhoRedeemedOffers(
    $page: Int!, 
    $pageSize: Int!, 
    $communityFilter: String, 
    $searchText: String,
    $sortBy: String
    $sortOrder: String
  ) {
    findCustomersWhoRedeemedOffers(
      page: $page, 
      pageSize: $pageSize, 
      communityFilter: $communityFilter, 
      searchText: $searchText,
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        customer {
          id
          avatarUrl
          firstname
          lastname
          email
          username
          phone
          community {
            id
            name
            alias
          }
          createdAt
          updatedAt
          address
          gender
          dob
          nin
        }
        totalSpent
        location
      }
      pageSize
      page
      total
      totalPages
    }
  }
`;

interface CustomerWhoRedeemedOfferData {
  customer: Customer;
  totalSpent: number;
}

interface WithFindCustomersWhoRedeemedOffers {
  customers: CustomerWhoRedeemedOfferData[];
  loading: boolean;
  loadMore: () => void;
  hasMoreCustomers: boolean;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface CustomerProps {
  variables: FindCustomersWhoRedeemedOffersQueryVariables;
  children: (data: WithFindCustomersWhoRedeemedOffers) => React.ReactElement | null;
}

export const FindCustomersWhoRedeemedOffers: React.FC<CustomerProps> = ({ children, variables }) => {
  const { data, loading, fetchMore } = useQuery<
    FindCustomersWhoRedeemedOffersQuery, 
    FindCustomersWhoRedeemedOffersQueryVariables
  >(
    FIND_CUSTOMERS_WHO_REDEEMED_OFFERS,
    {
      variables,
      notifyOnNetworkStatusChange: true,
    }
  );

  // Now customers is an array of { customer, totalSpent }
  const customers = data?.findCustomersWhoRedeemedOffers?.data || [];
  const page = data?.findCustomersWhoRedeemedOffers?.page || variables.page || 1;
  const pageSize = data?.findCustomersWhoRedeemedOffers?.pageSize || variables.pageSize || 10;
  const total = data?.findCustomersWhoRedeemedOffers?.total || 0;
  const totalPages = data?.findCustomersWhoRedeemedOffers?.totalPages || 0;
  const hasMoreCustomers = page < totalPages;

  const loadMore = React.useCallback(() => {
    if (!hasMoreCustomers) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.findCustomersWhoRedeemedOffers) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findCustomersWhoRedeemedOffers: {
            ...fetchMoreResult.findCustomersWhoRedeemedOffers,
            data: [
              ...(prev?.findCustomersWhoRedeemedOffers?.data || []),
              ...(fetchMoreResult.findCustomersWhoRedeemedOffers.data || [])
            ]
          }
        };
      }
    });
  }, [fetchMore, variables, page, hasMoreCustomers]);

  return children({
    customers: customers as CustomerWhoRedeemedOfferData[],
    loading,
    loadMore,
    hasMoreCustomers,
    page,
    pageSize,
    total,
    totalPages,
  });
};